import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaUser, FaSignOutAlt, FaCheckCircle, FaUpload } from 'react-icons/fa';
import { registrationAPI } from '../../api';
import './ConferenceRegistration.css';

const REGISTRATION_DEADLINE = new Date('2026-06-15T23:59:59.999+05:30');
const REGISTRATION_DEADLINE_LABEL = '15 June 2026, 11:59 PM IST';

const ConferenceRegistration = () => {
  const [user, setUser] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    participantCategory: '',
    transactionDetails: ''
  });
  const [paymentProofFile, setPaymentProofFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState(null);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [registrationType, setRegistrationType] = useState('regular');
  const navigate = useNavigate();

  const CATEGORIES = {
    'PG Students & Research Scholars': {
      regular: 750,
      late: 1500
    },
    'Postdocs & Research Associates': {
      regular: 750,
      late: 1500
    },
    'Faculty from Academia': {
      regular: 1000,
      late: 2000
    },
    'Industry & R&D Representatives': {
      regular: 1500,
      late: 3000
    }
  };

  const BANK_DETAILS = {
    accountName: 'IIITDM KURNOOL',
    accountNumber: '756701001024',
    ifscCode: 'ICIC0007567',
    bankName: 'ICICI BANK',
    branch: 'C-Camp Kurnool',
    micrCode: '518229009'
  };

  useEffect(() => {
    const loggedInUser = localStorage.getItem('user');
    if (loggedInUser) {
      const user = JSON.parse(loggedInUser);
      setUser(user);
      
      // Auto-determine registration type based on current date
      const currentDate = new Date();
      const type = currentDate <= REGISTRATION_DEADLINE ? 'regular' : 'late';
      setRegistrationType(type);
      
      const fetchRegistration = async () => {
        const result = await registrationAPI.getRegistration(user.username);
        if (result.success && result.registration) {
          setRegistrationStatus(result.registration);
          setStep(3);
        }
      };
      
      fetchRegistration();
    } else {
      navigate('/login');
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  // REGISTRATION FUNCTIONS
  const calculateRegistrationCost = () => {
    if (formData.participantCategory) {
      return CATEGORIES[formData.participantCategory][registrationType] || 0;
    }
    return 0;
  };

  const getTotalAmount = () => {
    return calculateRegistrationCost();
  };

  const handleCategorySelect = (category) => {
    setFormData({
      ...formData,
      participantCategory: category
    });
    setStep(2);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!['application/pdf', 'image/jpeg', 'image/png'].includes(file.type)) {
        setError('Please select a PDF or image file only.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('File size exceeds 5MB. Please select a smaller file.');
        return;
      }
      setPaymentProofFile(file);
      setError('');
    }
  };

  const handleSubmitRegistration = async (e) => {
    e.preventDefault();

    if (!paymentProofFile) {
      setError('Please upload payment proof.');
      return;
    }

    setIsSubmitting(true);
    setError('');

    const result = await registrationAPI.submit(
      user.username,
      user.id,
      formData,
      paymentProofFile
    );

    setIsSubmitting(false);

    if (result.success) {
      setRegistrationStatus({
        participantCategory: formData.participantCategory,
        registrationType: registrationType,
        registrationAmount: calculateRegistrationCost(),
        totalAmount: getTotalAmount(),
        paymentProofFile: paymentProofFile.name
      });
      setStep(3);
    } else {
      setError(result.message || 'Failed to submit registration. Please try again.');
    }
  };

  if (!user) return null;

  return (
    <div className="conf-registration-page">
      {/* Navbar */}
      <header className="conf-registration-navbar">
        <div className="conf-registration-navbar-container">
          <h2 className="conf-registration-navbar-brand">Conference Registration</h2>
          <div className="conf-registration-navbar-right">
            <div className="conf-registration-profile-menu">
              <button
                className="conf-registration-profile-btn"
                onClick={() => setShowProfileDropdown(!showProfileDropdown)}
              >
                <FaUser /> {user.username || user.name}
              </button>
              {showProfileDropdown && (
                <div className="conf-registration-dropdown-menu">
                  <div className="conf-registration-dropdown-header">
                    <h4>{user.title} {user.lastName}</h4>
                    <p>{user.email}</p>
                  </div>
                </div>
              )}
            </div>
            <button
              className="conf-registration-logout-btn"
              onClick={handleLogout}
              title="Logout"
            >
              <FaSignOutAlt /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="conf-registration-container">
        {/* Registration Instructions Section */}
        <section className="conf-registration-instructions">
          <h2>Registration Instructions</h2>
          <ol>
            <li>All participants should register on or before <strong>{REGISTRATION_DEADLINE_LABEL}</strong></li>
            <li>One registered participant can present only one paper, but can be co-author in multiple papers</li>
            <li>Select your participant category - fees are automatically determined based on registration date:
              <ul>
                <li><strong>Regular Fee:</strong> Until {REGISTRATION_DEADLINE_LABEL}</li>
                <li><strong>Late Fee:</strong> After {REGISTRATION_DEADLINE_LABEL}</li>
              </ul>
            </li>
            <li>Complete payment using provided bank details</li>
            <li>Upload the bank transaction receipt as proof of payment</li>
            <li><strong>No cancellation of registration fee is possible</strong></li>
          </ol>
        </section>

        {step === 1 && !registrationStatus && (
          <section className="conf-registration-step">
            <h2>Step 1: Select Your Category</h2>
            <div className="conf-category-grid">
              {Object.keys(CATEGORIES).map(category => (
                <div
                  key={category}
                  className="conf-category-card"
                  onClick={() => handleCategorySelect(category)}
                >
                  <h3>{category}</h3>
                </div>
              ))}
            </div>
          </section>
        )}

        {step === 2 && !registrationStatus && (
          <section className="conf-registration-step">
            <h2>Step 3: Payment</h2>

            <div className="conf-cost-summary">
              <h3>Registration Summary</h3>
              <div className="conf-cost-item">
                <span>Participant Category:</span>
                <span><strong>{formData.participantCategory}</strong></span>
              </div>
              <div className="conf-cost-item">
                <span>Registration Type:</span>
                <span><strong>{registrationType === 'regular' ? `Regular (Until ${REGISTRATION_DEADLINE_LABEL})` : `Late (After ${REGISTRATION_DEADLINE_LABEL})`}</strong></span>
              </div>
              <div className="conf-cost-item">
                <span>Registration Fee:</span>
                <span>₹{calculateRegistrationCost()}</span>
              </div>
              <div className="conf-cost-item total">
                <span>Total Amount to Pay:</span>
                <span className="conf-total-amount">₹{getTotalAmount()}</span>
              </div>
            </div>

            <div className="conf-bank-details-section">
              <h3>Bank Details for Online Payment</h3>
              <div className="conf-bank-details-grid">
                <div className="conf-bank-details-card">
                  <div className="conf-bank-detail-item">
                    <label>Bank Name</label>
                    <p>{BANK_DETAILS.bankName}</p>
                  </div>
                  <div className="conf-bank-detail-item">
                    <label>Account Name</label>
                    <p>{BANK_DETAILS.accountName}</p>
                  </div>
                  <div className="conf-bank-detail-item">
                    <label>Account Number</label>
                    <p>{BANK_DETAILS.accountNumber}</p>
                  </div>
                  <div className="conf-bank-detail-item">
                    <label>IFSC Code</label>
                    <p>{BANK_DETAILS.ifscCode}</p>
                  </div>
                  <div className="conf-bank-detail-item">
                    <label>MICR Code</label>
                    <p>{BANK_DETAILS.micrCode}</p>
                  </div>
                  <div className="conf-bank-detail-item">
                    <label>Branch</label>
                    <p>{BANK_DETAILS.branch}</p>
                  </div>
                </div>
                <div className="conf-qr-code-card">
                  <h4>Payment QR Code</h4>
                  <div className="conf-qr-code-container">
                    <img src="/qr.png" alt="Payment QR Code" className="conf-qr-code-image" />
                  </div>
                </div>
              </div>
              <p className="conf-bank-note">
                ⚠️ Once payment is transferred, get the acknowledgement receipt from your bank containing transaction details.
              </p>
            </div>

            <div className="conf-payment-proof-section">
              <h3>Upload Payment Receipt</h3>
              <p className="conf-proof-subtitle">Upload a screenshot or PDF of your bank transaction receipt</p>

              {error && (
                <div className="conf-error-message">
                  {error}
                </div>
              )}

              <div className="conf-file-upload-area">
                <label htmlFor="payment-proof" className="conf-file-upload-label">
                  <FaUpload className="conf-upload-icon" />
                  <span>{paymentProofFile ? paymentProofFile.name : 'Click to upload payment receipt'}</span>
                  <p className="conf-file-types">Accepted: PDF, JPEG, PNG (Max 5MB)</p>
                </label>
                <input
                  type="file"
                  id="payment-proof"
                  accept=".pdf,.jpg,.jpeg,.png"
                  onChange={handleFileChange}
                  className="conf-hidden-input"
                />
              </div>

              <textarea
                className="conf-transaction-details"
                placeholder="Enter transaction details (Transaction ID, Date, Time, etc.)"
                value={formData.transactionDetails}
                onChange={(e) => setFormData({ ...formData, transactionDetails: e.target.value })}
              />

              <button
                className="conf-registration-btn conf-registration-btn-primary"
                onClick={handleSubmitRegistration}
                disabled={isSubmitting || !paymentProofFile}
              >
                {isSubmitting ? 'Submitting...' : 'Submit Registration'}
              </button>
            </div>
          </section>
        )}

        {step === 3 && registrationStatus && (
          <section className="conf-registration-step">
            <div className="conf-success-container">
              <FaCheckCircle className="conf-success-icon" />
              <h2>Registration Successful!</h2>
              <p>Your registration has been submitted and is pending verification.</p>

              <div className="conf-registration-summary">
                <h3>Registration Details</h3>
                <div className="conf-summary-item">
                  <label>Participant Category:</label>
                  <p>{registrationStatus.participantCategory}</p>
                </div>
                <div className="conf-summary-item">
                  <label>Registration Type:</label>
                  <p>{registrationStatus.registrationType === 'regular' ? `Regular (Until ${REGISTRATION_DEADLINE_LABEL})` : `Late (After ${REGISTRATION_DEADLINE_LABEL})`}</p>
                </div>
                <div className="conf-summary-item">
                  <label>Registration Amount:</label>
                  <p>₹{registrationStatus.registrationAmount}</p>
                </div>
                <div className="conf-summary-item total">
                  <label>Total Amount:</label>
                  <p className="conf-total-amount">₹{registrationStatus.totalAmount}</p>
                </div>
                <div className="conf-summary-item">
                  <label>Payment Proof:</label>
                  <p>{registrationStatus.paymentProofFile}</p>
                </div>
              </div>

              <p className="conf-success-note">
                You will receive a confirmation email once your registration is verified by the admin.
              </p>

              <button
                className="conf-registration-btn conf-registration-btn-secondary"
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
};

export default ConferenceRegistration;
